'use client';

import { useMemo } from 'react';
import useFeatureFlagStore from 'src/stores/featureFlag/useFeatureFlagStore';
import { UIMarket } from '@drift/common';

interface UseIsIsolatedMarginEnabledForMarketOptions {
	marketSymbol?: string;
	marketIndex?: number;
}

/**
 * Hook to check if isolated margin is enabled for a specific market.
 * Checks both the global ENABLE_ISOLATED_POSITIONS flag and the market-specific
 * ISOLATED_MARGIN_ENABLED_MARKETS flag.
 *
 * @param options - Either marketSymbol or marketIndex to identify the market
 * @returns true if isolated margin is enabled for this market, false otherwise
 */
export function useIsIsolatedMarginEnabledForMarket({
	marketSymbol,
	marketIndex,
}: UseIsIsolatedMarginEnabledForMarketOptions): boolean {
	const isIsolatedPositionsEnabled = useFeatureFlagStore((s) =>
		s.flagEnabled('ENABLE_ISOLATED_POSITIONS')
	);
	const isolatedMarginEnabledMarketsFlag = useFeatureFlagStore(
		(s) => s.flags.ISOLATED_MARGIN_ENABLED_MARKETS
	);

	return useMemo(() => {
		// First check if isolated positions feature is enabled globally
		if (!isIsolatedPositionsEnabled) {
			return false;
		}

		// Resolve the market symbol
		let resolvedSymbol = marketSymbol;
		if (!resolvedSymbol && marketIndex !== undefined) {
			resolvedSymbol = UIMarket.createPerpMarket(marketIndex).symbol;
		}

		if (!resolvedSymbol) {
			return false;
		}

		// Check if the market is in the enabled list
		return isolatedMarginEnabledMarketsFlag.marketSymbols.includes(
			resolvedSymbol
		);
	}, [
		isIsolatedPositionsEnabled,
		isolatedMarginEnabledMarketsFlag.marketSymbols,
		marketSymbol,
		marketIndex,
	]);
}

export default useIsIsolatedMarginEnabledForMarket;
